import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, Renderer2} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  Scroll,
} from '@angular/router';
import {ViewportScroller} from '@angular/common';
import {Constants} from './admin-core/constants';
import {BackendThemeOptions} from "./admin-core/backend-theme-options";
import {SubscribersService} from "@coreservices";
import {POSyncNotificationService} from './admin-core/services/helpers/po-sync-notification.service';
import {ActivatedRoute, ActivatedRouteSnapshot} from '@angular/router';
import {ThemeService} from './admin-core/services';
import {CopyMenuService} from './shared/copy-menu/copy-menu.service';

@Component({
  standalone: false,
  selector: 'app-root',
  // Eagerly change-detected (Angular 18 behaviour).
  //
  // Angular 22 made OnPush the DEFAULT for every component: `ChangeDetectionStrategy`
  // gained `Eager`, and a component def is compiled with `onPush: changeDetection !==
  // Eager`. Anything that omits `changeDetection` is therefore CheckOnce, so a view is
  // only repainted when something marks it dirty (a DOM listener, markForCheck(),
  // a signal) — assigning to a plain field inside a subscribe callback no longer paints.
  // Because an unmarked OnPush view also stops the change-detection traversal, a global
  // tick cannot reach anything below it, so this flag has to be set on the whole chain
  // from the root down to the views that render fetched data (see the email module).
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements AfterViewInit {
  title = 'gatherco';
  isLoading: boolean = false;
  shouldShowHeader: boolean = false;
  /** True on the dedicated full-page /ai chat, where the floating widget is hidden
   *  (avoids two chats at once). */
  isAiPage: boolean = false;

  /** Path (URL without its query string) of the last navigation the scroll
   *  policy below acted on — see the `Scroll` branch in the constructor. */
  private lastScrolledPath = '';


  constructor(
    private renderer: Renderer2,
    private router: Router,
    public changeDetection: ChangeDetectorRef,
    public globals: BackendThemeOptions,
    public subscribersService: SubscribersService,
    private route: ActivatedRoute,
    // Eagerly instantiate so WebSocket room join and restore-on-reload
    // happen at app boot, not only when ContainerManagement is visited.
    private poSyncNotificationService: POSyncNotificationService,
    private themeService: ThemeService,
    private zone: NgZone,
    private scroller: ViewportScroller,
    copyMenu: CopyMenuService,
  ) {
    // Apply the persisted light/dark theme before the first paint.
    this.themeService.init();
    this.poSyncNotificationService.initialize();
    this.guardTooltipEdges();
    this.guardStrayFileDrops();
    // right-click → copy a client's name / email / phone, app-wide
    copyMenu.install();

    router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.isLoading = true;
          break;
        }
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.isLoading = false;
          var currentUrl = this.router.url;
          this.isAiPage = currentUrl.startsWith('/ai');
          var keys = Object.keys(localStorage);

          Constants.LOCALSTORAGE_REMOVE_ARRAY.forEach(element => {
            if (!currentUrl.includes(element)) {
              for (var i = 0, key; key = keys[i]; i++) {
                if (key.includes(element)) {
                  localStorage.removeItem(key);
                }
              }
            }
          });

          // Routes whose URL (kebab-case) differs from their localStorage key
          // prefix (camelCase) need an explicit mapping rather than a single token.
          Constants.LOCALSTORAGE_REMOVE_MAP.forEach(({ routeSegment, keyPrefix }) => {
            if (!currentUrl.includes(routeSegment)) {
              for (var i = 0, key; key = keys[i]; i++) {
                if (key.includes(keyPrefix)) {
                  localStorage.removeItem(key);
                }
              }
            }
          });

          if (event instanceof NavigationEnd) {
            // The route tree comes from the Router's own state, NOT from the injected
            // ActivatedRoute: at NavigationEnd `this.route.firstChild` still describes the route we
            // just left, so `hideHeader` lagged one navigation behind. That stayed invisible while
            // every trip to /login was a full document reload (on a fresh document the stale tree is
            // empty, which happens to hide the header). Once the session teardown started routing
            // there in-app instead of reloading, the nav bar and the AI widget stayed on the login
            // screen. routerState.snapshot is always the tree that has just been activated.
            const hideHeader = this.hasHideHeaderData(this.router.routerState.snapshot.root.firstChild);
            this.shouldShowHeader = !hideHeader;
          }
          break;
        }
        default: {
          break;
        }
      }

      /* Manual scroll policy — `scrollPositionRestoration` is 'disabled' in the
         routing config, so nothing scrolls the page unless we do it here:
           · back / forward (a popstate carries `event.position`) → restore it
           · a different PATH → a new page, so go to the top
           · the same path with only the query params changed (the record
             screens' `?tab=` section switches) → leave the scroll alone.

         `anchorScrolling` is still handled by the router itself; a Scroll event
         raised for a fragment carries no position and the path is unchanged, so
         this branch does nothing and does not fight it. */
      if (event instanceof Scroll) {
        const url = event.routerEvent instanceof NavigationEnd
          ? event.routerEvent.urlAfterRedirects
          : event.routerEvent.url;
        const path = url.split('?')[0];
        if (event.position) {
          this.scroller.scrollToPosition(event.position);
        } else if (path !== this.lastScrolledPath) {
          this.scroller.scrollToPosition([0, 0]);
        }
        this.lastScrolledPath = path;
      }
    });
  }

  /**
   * Viewport-edge safety for the CSS tooltips (`.tip` + `data-tip`), app-wide.
   *
   * The bubble is a centred `::after`, so an anchor near either page edge would
   * render it partly off-screen — which is most visible at the responsive
   * breakpoints, where the same rails and toolbars sit much closer to the edge.
   * On hover we measure the rendered bubble (it exists at opacity 0) and flip
   * its anchoring when it would spill; `.tip-edge` / `.tip-edge-start` in
   * styles.css do the re-anchoring.
   *
   * Ported from console-v2 (app.component.ts), with two changes:
   *
   * 1. The listener runs OUTSIDE Angular. console-v2 is zoneless, this app is
   *    not, so a zone-patched `mouseover` handler would kick off a change-
   *    detection pass on every pointer move across the document. It only
   *    touches classList, so it never needs one.
   *
   * 2. It measures against the nearest CLIPPING ANCESTOR, not the viewport.
   *    console-v2 can ask about the viewport because nothing between its
   *    tooltips and the page clips: its rails are `position: sticky` and the
   *    page itself scrolls. This app's mailbox is built the other way round —
   *    `.m-main` owns the scrolling (which is what holds the rails still) and
   *    `.mail-wrap` carries `overflow-x: clip` — so a bubble can sit well
   *    inside the viewport and still be cut off at a container edge. Measured
   *    on the listing: the triage-star label overhung that edge by 42px and the
   *    select-all label by 22px, at every width from 768 up, while the
   *    viewport test saw nothing wrong.
   *
   * Keyboard focus is guarded too: `.tip:focus-visible` shows the same bubble
   * (styles.css), so it needs the same flip.
   */
  private guardTooltipEdges(): void {
    const MARGIN = 12;
    /* The gap every tip sheet leaves between the control and the bubble
       (`calc(100% + 9px)`), so "does it fit on the other side" is measured
       against where the bubble would actually land. */
    const GAP = 9;
    /* The "Liked by" popover's own offset from its button (`calc(100% + 10px)`). */
    const LIKERS_GAP = 10;

    /* The box the bubble actually has to fit inside: the first ancestor that
       clips horizontally, intersected with the viewport. `overflow-x` is what
       matters, and it is rarely written directly — `overflow-y: auto` alone
       computes `overflow-x` to `auto` rather than leaving it visible, which is
       exactly how `.m-main` became a horizontal clipper. */
    const clipBounds = (el: HTMLElement): { left: number; right: number } => {
      for (let p = el.parentElement; p && p !== document.documentElement; p = p.parentElement) {
        if (getComputedStyle(p).overflowX === 'visible') {
          continue;
        }
        const box = p.getBoundingClientRect();
        return { left: Math.max(0, box.left), right: Math.min(window.innerWidth, box.right) };
      }
      return { left: 0, right: window.innerWidth };
    };

    /* The same question on the other axis. A bubble that opens downward from a
       control near the FOOT of a scroller is cut in half by that scroller, and
       nothing above measured it: the internal-notes card (`.notes-scroll`,
       `overflow-y: auto`) chopped "Pin note", "Reply in thread", "Preview file"
       and "Delete file" on whichever note happened to sit at the bottom of the
       visible list. It is a scroll box, not the last DOM row, so a CSS
       `:last-child` flip could not have covered it. */
    const clipBoundsY = (el: HTMLElement): { top: number; bottom: number } => {
      for (let p = el.parentElement; p && p !== document.documentElement; p = p.parentElement) {
        if (getComputedStyle(p).overflowY === 'visible') {
          continue;
        }
        const box = p.getBoundingClientRect();
        return { top: Math.max(0, box.top), bottom: Math.min(viewportHeight(), box.bottom) };
      }
      return { top: 0, bottom: viewportHeight() };
    };

    /* `innerHeight` reads 0 in an offscreen/headless render, and a 0 floor would
       make every downward bubble look clipped. Fall back rather than decide on a
       bogus number — an unknown viewport means "do not flip on the viewport's
       account", which leaves the container test to do the work. */
    const viewportHeight = (): number =>
      window.innerHeight || document.documentElement.clientHeight || Infinity;

    /* Outer height of the bubble. `height` alone is the CONTENT box (the tip
       sheets do not set `box-sizing` on `::after`), and the bubble carries
       8px+8px padding and a 1px hairline — 18px of a ~35px bubble. */
    const outerHeight = (style: CSSStyleDeclaration): number => {
      const height = parseFloat(style.height);
      if (!Number.isFinite(height)) {
        return NaN;
      }
      if (style.boxSizing === 'border-box') {
        return height;
      }
      return height
        + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
        + (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
    };

    this.zone.runOutsideAngular(() => {
      /* Mark the document touch-driven on the FIRST real touch. styles.css reveals
         a tapped `.tip` bubble under `html.touch-tips` — a reliable signal that
         Chrome DevTools device-mode DOES produce (it fires touchstart), unlike the
         `@media (hover: none)` query it also keys off, which device-mode leaves
         reading the host desktop. Desktop never fires touchstart, so it never gets
         the class and a mouse click's `.tip-open` keeps painting nothing there. */
      document.addEventListener('touchstart', () => {
        document.documentElement.classList.add('touch-tips');
      }, { passive: true, once: true });

      const place = (event: Event) => {
        const tip = (event.target as Element | null)?.closest?.('.tip') as HTMLElement | null;
        if (!tip || !tip.getAttribute('data-tip')) {
          return;
        }
        tip.classList.remove('tip-edge', 'tip-edge-start', 'tip-flip-up', 'tip-flip-down');
        const after = getComputedStyle(tip, '::after');
        const bubble = parseFloat(after.width) || 250;
        const rect = tip.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const bounds = clipBounds(tip);
        if (center + bubble / 2 > bounds.right - MARGIN) {
          tip.classList.add('tip-edge');
        } else if (center - bubble / 2 < bounds.left + MARGIN) {
          tip.classList.add('tip-edge-start');
        }

        /* ── vertical, same idea ────────────────────────────────────────
           Which way the bubble opens is READ, never assumed: some sheets
           open it upward by default (the listings), some downward (the
           note cards), and `.tip-down` / `.tbl` flip it again — so the used
           `top` of the ::after box is the only honest answer. It is
           measured relative to the tip's own box, so it is negative when
           the bubble sits above and positive when it sits below.

           A flip is applied ONLY when the opposite side actually has room:
           in a box too short for the bubble either way, moving it just
           swaps which end is cut, and the resting placement is the one the
           component author chose. `NaN` (no bubble laid out) falls through
           the same way and changes nothing. */
        const height = outerHeight(after);
        const offset = parseFloat(after.top);
        if (!Number.isFinite(height) || !Number.isFinite(offset)) {
          return;
        }
        const vertical = clipBoundsY(tip);
        const opensDown = offset >= 0;
        const bubbleTop = rect.top + offset;
        const fitsAbove = rect.top - GAP - height >= vertical.top + MARGIN;
        const fitsBelow = rect.bottom + GAP + height <= vertical.bottom - MARGIN;
        if (opensDown && bubbleTop + height > vertical.bottom - MARGIN && fitsAbove) {
          tip.classList.add('tip-flip-up');
        } else if (!opensDown && bubbleTop < vertical.top + MARGIN && fitsBelow) {
          tip.classList.add('tip-flip-down');
        }
      };
      document.addEventListener('mouseover', place, { passive: true });
      document.addEventListener('focusin', place, { passive: true });

      /* ── the internal-note "Liked by" popover, same problem ───────────
         `.nf-likers` is a real element rather than a `::after`, but it is
         anchored the same way (upward from its like button) and clipped by
         the same `.notes-scroll` box, so the list was sliced off at the top
         whenever the note being hovered sat at the head of the visible
         list. styles.css already carries the flipped variant,
         `.nf-likers--down`; it was only ever applied to the FIRST note via
         a template binding, which is the wrong test for a scroll box — the
         note at the top of the VIEW is rarely the first note in the list.

         That binding is left in place as the no-JS default; this measures
         and corrects it, and it is the only thing covering the two
         Add-Account / Add-Contact screens, whose markup never had it.

         `offsetHeight`, not the bounding rect: the popover's resting state
         carries `translateY(4px) scale(.98)`, which would put a couple of
         percent of error into a measurement taken from the rect. Layout
         height is placement- and transform-independent. */
      const placeLikers = (event: Event) => {
        const wrap = (event.target as Element | null)?.closest?.('.nf-like-wrap') as HTMLElement | null;
        const card = wrap?.querySelector('.nf-likers') as HTMLElement | null;
        if (!card) {
          return;
        }
        card.classList.remove('nf-likers--down');
        const height = card.offsetHeight;
        if (!height) {
          return;
        }
        const anchor = wrap!.getBoundingClientRect();
        const bounds = clipBoundsY(wrap!);
        const clippedAbove = anchor.top - LIKERS_GAP - height < bounds.top + MARGIN;
        const fitsBelow = anchor.bottom + LIKERS_GAP + height <= bounds.bottom - MARGIN;
        if (clippedAbove && fitsBelow) {
          card.classList.add('nf-likers--down');
        }
      };
      /* `pointerdown` as well as hover/focus: on touch the popover is pinned
         open by a long-press, which never produces a `mouseover`. */
      document.addEventListener('mouseover', placeLikers, { passive: true });
      document.addEventListener('focusin', placeLikers, { passive: true });
      document.addEventListener('pointerdown', placeLikers, { passive: true });

      /* ── the same label, for a touch tap ──────────────────────────────
         On a touch device there is no hover, and most mobile browsers do
         not apply `:focus` / `:focus-visible` to a <button> on tap, so the
         info-icon bubbles (`.tip[data-tip]`) never appeared — the whole
         reason they show nothing on mobile. A tap on a `.tip` toggles
         `.tip-open` (which styles.css reveals ONLY under `@media (hover:
         none)`); a tap anywhere else closes it. Edge anchoring runs first
         so a tapped bubble is flipped away from the clipping edge exactly
         as a hovered one is.

         The listener is attached unconditionally — NOT gated on a startup
         `(hover: none)` check — so it also covers a devtools device-mode
         toggle AFTER load and real touch devices without a reload. It stays
         desktop-safe because the CSS reveal is behind the media query: a
         mouse click still adds `.tip-open`, but that class paints nothing on
         a hover-capable pointer, so desktop keeps hover as its only trigger. */
      const closeOpen = () =>
        document.querySelectorAll('.tip.tip-open').forEach(t => t.classList.remove('tip-open'));

      /* ── which tips latch open on a tap, and which must not ────────────
         `.tip-open` exists for a HINT: an ⓘ whose whole purpose is the
         bubble, which a touch user has no other way to read. It is wrong for
         an ACTION control, and that was the bug — tapping "Edit reply" on a
         phone opened the reply composer AND left the black "Edit reply"
         label parked on top of it until the next tap somewhere else. The
         label answered a question the user had already answered by tapping.

         Element type is the test, because it is the only honest one
         available: whether an Angular `(click)` is bound to a node is not
         readable from the DOM. Buttons, links, labels and form controls do
         something when tapped; a `<span>` or `<div>` carrying a `data-tip`
         is a hint and nothing else. That covers 159 of the 160 action tips
         in the app.

         `data-tip-hold` is the opt-out for the handful of controls that ARE
         hint-only despite being buttons — the ⓘ next to ABN, Email, Address
         line 1, and the info icons in the order-summary quick view. They are
         buttons for keyboard and screen-reader reasons, not because they act.

         Desktop is untouched either way: `.tip-open` paints nothing on a
         hover-capable pointer (see styles.css), where hover is the trigger. */
      const TIP_CONTROLS = 'button, a, label, input, select, textarea, summary,'
        + '[role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="checkbox"]';
      const holdsOpenOnTap = (tip: HTMLElement) =>
        tip.hasAttribute('data-tip-hold') || !tip.matches(TIP_CONTROLS);

      document.addEventListener('click', (event) => {
        const tip = (event.target as Element | null)?.closest?.('.tip') as HTMLElement | null;
        if (!tip || !tip.getAttribute('data-tip')) {
          closeOpen();
          return;
        }
        const wasOpen = tip.classList.contains('tip-open');
        closeOpen();
        if (!wasOpen && holdsOpenOnTap(tip)) {
          place(event);
          tip.classList.add('tip-open');
        }
      }, { passive: true });

      /* ── directive tooltips: ANY click dismisses EVERY open one ───────────
         The other mechanism on this page. `.tip` / `data-tip` above is CSS-only
         and closes itself; `[tooltip]` is a directive that renders a bubble into
         the DOM and closes it on the CLOSE half of its own triggers — `mouseout`
         for `hover`, `focusout` for `focus` (DEFAULT_ALIASES in ngx-bootstrap's
         utils). A click frequently produces NEITHER:

           • the dialog it opens appears under a stationary cursor, and inserting
             an element beneath a pointer that does not move fires no pointer
             event at all;
           • an action that re-lays out the page slides the control out from
             under the cursor, which fires nothing either;
           • the control may be destroyed, or replaced by a different one, while
             the bubble it owns is still on screen.

         So the label stayed parked over the page — over a dialog, over the notes
         feed, over whatever the click opened — until the user happened to hover
         that exact control and leave it again. Reported three times over
         (GC-351): stuck "Delete File" / "View File" in the note dialog, stuck
         "Upload File" on the reply composer's paperclip, and stuck "Edit
         Internal Note" on a reply.

         This is the app-wide net, and it is why it lives here rather than on the
         controls: the individual `#tip="bs-tooltip"` + `.hide()` calls (the house
         pattern, in internal-notes, external-notes, milestones-status and the
         note dialogs) each fix ONE button, and the requirement is that no label
         survives ANY click — including Cancel, a backdrop, or a control nobody
         has thought to annotate yet.

         HOW it closes them, and why not more directly:

         `aria-describedby="tooltip-<id>"` is set on the TRIGGER for exactly as
         long as its bubble is open (`setAriaDescribedBy`, called from the
         directive's own onShown/onHidden), so it is a reliable public marker for
         "this element currently owns an open tooltip" — no directive instances to
         reach, no library internals, nothing per-module to register.

         Re-dispatching the library's own close events on that trigger runs the
         library's own hide path, which is what keeps its state consistent.
         Removing the bubble's DOM node instead would NOT work: the loader's
         `isShown` would stay true and `show()` early-returns on it, so that label
         could never be shown again.

         `bubbles: false`, because `listenToTriggersV2` binds the close listener
         to the trigger ITSELF — the target phase is enough, and a bubbling
         synthetic `mouseout` would run every ancestor's mouse handler on the way
         up. Both `mouseout`/`focusout` (what the library binds) and
         `mouseleave`/`blur` (what a future version might bind) are sent, so this
         cannot silently stop working on an upgrade.

         CAPTURE phase, because several of these controls stop propagation in
         their own handler — `internal-notes`' Add Note calls
         `stopImmediatePropagation()` — and a bubble-phase listener would never
         run for them. `pointerdown` as well as `click`: it dismisses on the way
         DOWN so the label goes at once, and the `click` pass then catches the
         case where the tooltip was not open at pointerdown and the button's own
         focus (mousedown → focus → click) raised it in between.

         Both tooltip libraries in this app are covered, because both mark their
         trigger the same way while open and both close on the same events:
         ngx-bootstrap writes `tooltip-<n>` (`hover: [mouseover, mouseout]`),
         ng-bootstrap writes `ngb-tooltip-<n>` (`hover: [mouseenter, mouseleave]`).
         An `ngb-tooltip-3` does not start with `tooltip-`, hence two selectors. */
      const OPEN_TOOLTIP_TRIGGERS =
        '[aria-describedby^="tooltip-"], [aria-describedby^="ngb-tooltip-"]';
      const closeOpenTooltips = () => {
        document.querySelectorAll<HTMLElement>(OPEN_TOOLTIP_TRIGGERS)
          .forEach((trigger) => {
            for (const type of ['mouseout', 'mouseleave', 'focusout', 'blur']) {
              trigger.dispatchEvent(new Event(type, { bubbles: false }));
            }
          });
      };
      document.addEventListener('pointerdown', closeOpenTooltips, { capture: true, passive: true });
      document.addEventListener('click', closeOpenTooltips, { capture: true, passive: true });

      /* ── …and the case a click-time dismiss CANNOT fix ─────────────────────
         Dismissing on click is not enough, and this is the part every earlier
         attempt missed — including the two above on their own.

         Measured with real mouse input through CDP, hovering a control and then
         clicking it:

           hover        → mouseover                        → label shown
           click        → pointerdown, focus, focusin, click, mouseout, mouseleave
           dialog opens → focus moves INTO the dialog       → focusout on the trigger

         So far so good: `mouseout` really does fire when the dialog covers the
         trigger — the browser re-hit-tests on the overlay, no pointer movement
         needed — and the label goes. The damage happens on the way OUT:

           click Cancel → dialog closes and RESTORES FOCUS TO THE OPENER
                        → focusin on the trigger, pointer nowhere near it
                        → `focus` is half of the default `triggers: 'hover focus'`
                        → the label is shown AGAIN, and now nothing can close it:
                          no `mouseout` can come (the pointer is not there) and no
                          `focusout` until the user focuses something else.

         That is the reported bug, in one line: the label appears when the dialog
         CLOSES, which is why dismissing on click — on the opener, or on Cancel —
         is always too early. Both ng-bootstrap and ngx-bootstrap modals restore
         focus to their opener, so it applies to every dialog in the app.

         The gate is `:focus-visible`, which is exactly the distinction needed: a
         restored focus and a mouse-click focus are NOT focus-visible, while a Tab
         is. Measured on the same harness — baseline vs this listener:

           click Cancel → restored : bubble VISIBLE  →  bubble hidden
           Tab to the control      : bubble VISIBLE  →  bubble VISIBLE (kept)

         So keyboard users keep their labels and no unasked-for focus can raise
         one. BUBBLE phase, deliberately: the libraries bind their own `focusin`
         show listener to the trigger, so this has to run after it, not before.

         `matches()` is guarded because a selector an engine does not know throws
         a SyntaxError — treated as "assume the user asked for it" so an exotic
         browser loses nothing.

         The composer's paperclip keeps its own `tipSuppressed` guard on top of
         this (note-attachment-picker): an OS file dialog is a WINDOW focus
         change, not a DOM one, and whether Chrome replays `focusin` on the way
         out of one is not something this repo can measure — keystroke automation
         into a native dialog is blocked here. The control-level guard does not
         depend on the answer. */
      const asked = (el: Element): boolean => {
        try {
          return el.matches(':focus-visible');
        } catch {
          return true;
        }
      };
      document.addEventListener('focusin', (event) => {
        const el = event.target;
        if (el instanceof Element && !asked(el)) {
          closeOpenTooltips();
        }
      });
    });
  }

  /**
   * Swallow a file drop that MISSED its drop target.
   *
   * A drop is only "handled" by the element that cancels `dragover`. Everywhere
   * else the browser's default action for a dropped file is to OPEN it — i.e. to
   * navigate the tab to `file:///…`. That tears the whole SPA down: whatever was
   * half-written on screen is gone, and coming back lands on a freshly
   * bootstrapped app with none of it.
   *
   * It is easy to hit with the note composers, because their drop target is one
   * field inside a much larger surface: aiming a file at the reply box and
   * releasing 20px high — on the note body above it, on the thread, on the card —
   * used to take the page down with it, which reads exactly like "the reply
   * section disappeared and I lost what I typed".
   *
   * The email composer already guards its own surface this way
   * (`FileDropZoneDirective`, "so the browser never navigates to the dropped
   * file"), but those listeners live and die with that directive, so nothing
   * covered the note surfaces. This is the same rule, applied once for the app.
   *
   * Two listeners, both on the document, both in the BUBBLE phase so every real
   * target sees the event first:
   *
   *   - `dragover` is cancelled, which is what actually suppresses the
   *     navigation. Without cancelling it here the browser never even fires
   *     `drop` on a stray release — it goes straight to its default action.
   *   - `drop` is cancelled too, so the release itself does nothing at all.
   *
   * `defaultPrevented` is the test for "a real target already claimed this",
   * which is why this cannot interfere with the pickers: `bindFileDropTarget`,
   * `appUploadFile` and every other drop target in the app cancel `dragover` on
   * their own element, so their event reaches this listener already prevented.
   *
   * A drag carrying NO files is left completely alone. Quill has its own
   * drag-and-drop for text (dragging a selection to move it, or dragging text in
   * from another window) and cancelling every drag here would silently take that
   * away — the same rule `bindFileDropTarget` keeps.
   *
   * Outside Angular: neither handler touches a binding, and `dragover` fires
   * continuously while the pointer moves, so a zone-patched listener would mean a
   * change-detection pass per mouse move of every drag on the page.
   */
  private guardStrayFileDrops(): void {
    const hasFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const swallow = (event: DragEvent) => {
      if (event.defaultPrevented || !hasFiles(event)) {
        return;
      }
      event.preventDefault();
    };

    this.zone.runOutsideAngular(() => {
      document.addEventListener('dragover', swallow as EventListener);
      document.addEventListener('drop', swallow as EventListener);
    });
  }

  /**
   * True when the activated route — or anything under it — asks for the shell to be hidden
   * (`data: {hideHeader: true}`, which every auth-module route sets). A missing route also counts as
   * "hide", which is what keeps the header off the screen on the very first paint.
   */
  private hasHideHeaderData(route: ActivatedRouteSnapshot | null): boolean {
    if (!route || route.data && route.data['hideHeader']) {
      return true;
    }

    if (route.firstChild) {
      return this.hasHideHeaderData(route.firstChild);
    }

    return false;
  }

  ngAfterViewInit() {
  }
}
